import clsx from 'clsx'
import React, { ComponentProps, forwardRef, useId } from 'react'

const flowerCenterX = 385
const flowerCenterY = 280

const petalRadius = 105
const petalInnerRadius = 67
const flowerPetalOffsetY = flowerCenterY - (petalRadius + petalInnerRadius)

const petal2Rotation = 360 / 7
const petal3Rotation = (360 * 2) / 7
const petal4Rotation = (360 * 3) / 7
const petal5Rotation = (360 * 4) / 7
const petal6Rotation = (360 * 5) / 7
const petal7Rotation = (360 * 6) / 7

const baseAnimationClasses = 'transition-all ease-in-out'

export interface AnimatedLogoProps extends Omit<ComponentProps<'svg'>, 'viewBox' | 'xmlns'> {
  keepScale?: boolean
  expanded?: boolean
}

export const AnimatedLogo = forwardRef<SVGSVGElement, AnimatedLogoProps>(
  ({ className = '', keepScale = false, expanded, id: propsId, ...props }, ref) => {
    const genId = useId()
    const id = propsId ?? genId

    return (
      <svg
        id={id}
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 770 559"
        className={clsx(
          `duration-500 fill-current`,
          baseAnimationClasses,
          className,
          expanded || keepScale
            ? 'scale-100 translate-y-0 ease-[cubic-bezier(0.3,2,0.21,0.85)]'
            : 'scale-125 translate-y-[20%] ease-[cubic-bezier(0.79,0,0.6,-0.3)] delay-[1500ms]'
        )}
        {...props}
      >
        <mask id={`${id}-petal2clip`} x="-20%" y="-20%" width="140%" height="140%">
          <circle cx={flowerCenterX} cy={flowerPetalOffsetY} r={petalRadius} fill="white" />
          <circle cx={flowerCenterX} cy={flowerPetalOffsetY} r={petalInnerRadius} fill="black" />
          <circle
            className="duration-[2000ms] ease-in-out"
            cx={flowerCenterX}
            cy={flowerPetalOffsetY}
            r={petalInnerRadius}
            fill="black"
            style={{
              transform: `rotate(${expanded ? petal7Rotation : 0}deg)`,
              transformOrigin: `${flowerCenterX}px ${flowerCenterY}px`,
            }}
          />
          <circle
            className="duration-[2000ms] ease-in-out"
            fill="black"
            cx={flowerCenterX}
            cy={flowerPetalOffsetY}
            r={petalInnerRadius}
            style={{
              transform: `rotate(${expanded ? petal2Rotation : 0}deg)`,
              transformOrigin: `${flowerCenterX}px ${flowerCenterY}px`,
            }}
          />
        </mask>
        <mask id={`${id}-petal3clip`} x="-20%" y="-20%" width="140%" height="140%">
          <circle cx={flowerCenterX} cy={flowerPetalOffsetY} r={petalRadius} fill="white" />
          <circle cx={flowerCenterX} cy={flowerPetalOffsetY} r={petalInnerRadius} fill="black" />
          <circle
            className="duration-[2000ms] ease-in-out"
            fill="black"
            cx={flowerCenterX}
            cy={flowerPetalOffsetY}
            r={petalInnerRadius}
            style={{
              transform: `rotate(${expanded ? petal2Rotation : 0}deg)`,
              transformOrigin: `${flowerCenterX}px ${flowerCenterY}px`,
            }}
          />
        </mask>
        <mask id={`${id}-eightclip`} x="-20%" y="-20%" width="140%" height="140%">
          <circle cx={flowerCenterX} cy={flowerCenterY} r={petalRadius} fill="white" />
          <circle cx={flowerCenterX} cy={flowerCenterY} r={petalInnerRadius} fill="black" />
        </mask>
        <mask id={`${id}-petal1clip`} x="-20%" y="-20%" width="140%" height="140%">
          <circle cx={flowerCenterX} cy={flowerPetalOffsetY} r={petalRadius} fill="white" />
          <circle cx={flowerCenterX} cy={flowerPetalOffsetY} r={petalInnerRadius} fill="black" />
        </mask>
        <g className="[color:var(--8thday-petal-color,currentColor)]">
          <circle
            id={`${id}-petal2`}
            className={`${baseAnimationClasses} duration-[2000ms]`}
            style={{
              transform: `rotate(${expanded ? petal2Rotation : 0}deg)`,
              transformOrigin: `${flowerCenterX}px ${flowerCenterY}px`,
            }}
            cx={flowerCenterX}
            cy={flowerPetalOffsetY}
            r={petalRadius}
            mask={`url(#${id}-petal2clip)`}
          />
          <circle
            id={`${id}-petal3`}
            className={`${baseAnimationClasses} duration-[2000ms]`}
            cx={flowerCenterX}
            cy={flowerPetalOffsetY}
            r={petalRadius}
            style={{
              transform: `rotate(${expanded ? petal3Rotation : 0}deg)`,
              transformOrigin: `${flowerCenterX}px ${flowerCenterY}px`,
            }}
            mask={`url(#${id}-petal3clip)`}
          />
          <circle
            id={`${id}-petal4`}
            className={`${baseAnimationClasses} duration-[2000ms]`}
            cx={flowerCenterX}
            cy={flowerPetalOffsetY}
            r={petalRadius}
            style={{
              transform: `rotate(${expanded ? petal4Rotation : 0}deg)`,
              transformOrigin: `${flowerCenterX}px ${flowerCenterY}px`,
            }}
            mask={`url(#${id}-petal3clip)`}
          />
          <circle
            id={`${id}-petal5`}
            className={`${baseAnimationClasses} duration-[2000ms]`}
            cx={flowerCenterX}
            cy={flowerPetalOffsetY}
            r={petalRadius}
            style={{
              transform: `rotate(${expanded ? petal5Rotation : 0}deg)`,
              transformOrigin: `${flowerCenterX}px ${flowerCenterY}px`,
            }}
            mask={`url(#${id}-petal3clip)`}
          />
          <circle
            id={`${id}-petal6`}
            className={`${baseAnimationClasses} duration-[2000ms]`}
            cx={flowerCenterX}
            cy={flowerPetalOffsetY}
            r={petalRadius}
            style={{
              transform: `rotate(${expanded ? petal6Rotation : 0}deg)`,
              transformOrigin: `${flowerCenterX}px ${flowerCenterY}px`,
            }}
            mask={`url(#${id}-petal3clip)`}
          />
          <circle
            id={`${id}-petal7`}
            className={`${baseAnimationClasses} duration-[2000ms]`}
            cx={flowerCenterX}
            cy={flowerPetalOffsetY}
            r={petalRadius}
            style={{
              transform: `rotate(${expanded ? petal7Rotation : 0}deg)`,
              transformOrigin: `${flowerCenterX}px ${flowerCenterY}px`,
            }}
            mask={`url(#${id}-petal3clip)`}
          />
        </g>
        <g
          id={`${id}-eight`}
          className={`${baseAnimationClasses} duration-[2000ms] z-10 [color:var(--8thday-eight-color,var(--8thday-petal-color,currentColor))]`}
          style={{
            transform: `rotate(${expanded ? 360 : 0}deg)`,
            transformOrigin: `${flowerCenterX}px ${flowerCenterY}px`,
          }}
        >
          <circle
            id={`${id}-eight-petal`}
            className={`${baseAnimationClasses} duration-[2000ms]`}
            cx={flowerCenterX}
            cy={flowerPetalOffsetY}
            r={petalRadius}
            mask={`url(#${id}-petal1clip)`}
          />
          <circle
            id={`${id}-flower-center`}
            className={`${baseAnimationClasses} duration-[2000ms] [color:var(--8thday-center-color,var(--8thday-eight-color,var(--8thday-petal-color,currentColor)))]`}
            cx={flowerCenterX}
            cy={flowerCenterY}
            r={petalRadius}
            mask={`url(#${id}-eightclip)`}
          />
        </g>
        <g className="[color:var(--8thday-leaf-color,currentColor)]">
          <path
            className={`transition-all duration-[2000ms] ${
              expanded ? 'ease-[cubic-bezier(0.3,2,0.21,0.85)]' : 'ease-[cubic-bezier(0.79,0,0.6,-0.3)]'
            }`}
            style={{
              transform: expanded
                ? 'scale(1) rotate(0) translate(0, 0)'
                : 'scale(1.25) rotate(11.5deg) translate(100px, -45px)',
              transformOrigin: 'bottom center',
            }}
            d="m178.19,467.39c-3,6.83-5.71,8.96-8.57,10.84-6.48,4.27-24.04,5.32-35.88,3.9-.04,0-.06,0-.1,0-.66-.03-8.45-.39-17.83-3.32-14.94-4.66-17.66-7.62-19.6-9.19-5.11-4.13-9.71-7.16-17.04-17.62-7.09-10.11-5.53-7.52-10.02-15.51-4.24-7.54-3.86-7.6-8.87-16.66-.78-1.42-3.77-7.85-8.55-13.98-5.84-7.5-12.13-12.21-15.57-14.62-6.89-4.81-9.36-5.62-16.34-10.6-1.11-.79-4.53-2.66-7.66-6.26-3.77-4.33-4.23-6.42-5.47-9.87-1.56-4.36-2.03-7.58-1.43-11.7.7-4.79,1.22-5.29,4.02-9.64,2.17-3.37,3.04-4.12,5.3-7.72,2.55-4.09,1.72-2.43,6.32-9.83,2.87-4.62,4.11-7.82,6.32-16.79,1.91-7.77,2.38-10.6,2.62-12.26.81-5.58.94-4.89,1.6-11.55.27-2.79.8-6.73,1.66-12.57.35-2.37.12-6.68,1.77-11.26,1.99-5.52,1.21-5.14,2.19-8.15,1.76-5.42,1.51-6.59,3.26-12.32.9-2.96,1.62-4.91,3.57-10.09,1.27-3.38,2.89-5.25,4-7.74,1.09-2.44,2-3.69,3.7-6.57,1.6-2.72,3.3-4.93,6.19-7.87.61-.62,3.43-4.35,7.91-7.05,6.26-3.77,5.79-3.51,7.47-4.16,2.58-1,4.12-2.5,8.49-3.77,7.02-2.03,3.3-.95,7.6-1.91,1.8-.41,7.21-1.33,14.43-.77,2.47.19,4.25.56,6.96,2.49,2.93,2.08,4.69,4.44,5.62,6,2.53,4.26,2.65,6.69,2.74,7.72.3,3.22.36,5.92-1.02,9.32-.96,2.34-.8,2.62-4.28,6.32-1.98,2.11-3.32,2.94-5.68,4.53-3.78,2.55-5.49,2.88-8.95,4.6-.14.07-.28.12-.43.14-.54.1-1.82.61-7.28,3.23l-3.26,2.46c-3.19,3-3.33,3.02-4.95,4.84-.06.07-.11.14-.16.21-.27.43-1.45,2.2-4.96,6.19-.05.06-.1.12-.14.19l-1.76,2.73s-.04.06-.06.09c-2.23,3.97-1.74,4.47-3.51,6.83-.1.13-.18.28-.24.44-.19.56-.74,2.06-1.55,3.44-.06.1-.11.21-.15.33l-1.62,5.38c-.02.06-.04.12-.07.18-1.46,3.22-.94,4.74-2.4,7.95-.03.07-.06.15-.08.23l-.47,1.77s-.02.07-.03.1c-1.65,4.94-2.24,6.94-2.75,9.5-.6,2.99-.63,3.03-1,6.53s-.56,4.57-1.21,8.55c-.87,5.29-.78,4.58-1.66,10.09-.49,3.07-.91,7.28-.95,7.74,0,.03,0,.06-.01.09l-.81,5.13s-.01.07-.01.11c-.23,2.77-.57,5.11-.63,5.5,0,.04,0,.08-.01.13-.2,3.38-.38,5.21-.59,7.39,0,.04,0,.07,0,.11-.01.48-.1,4.27-.1,9.17,0,.16.03.33.08.49l1.61,5c.03.09.06.17.11.25,2.05,3.96,3.03,4.56,4.22,6.67.07.13.17.25.27.36.37.37,1.54,1.7,4.42,6.21.02.02.03.05.05.07l2.94,4.07s.04.06.06.09c2.51,4.02,3,4.46,4.5,6.91,3.64,5.92,2.95,4.77,4.6,7.47s1.73,3.42,3.57,6.26c1.51,2.31,3.24,4.79,5.04,7.66,2.53,4.04,8.46,11.83,9.08,12.63.04.05.07.08.11.13.93.96,12.72,13.2,13.79,14.37,2.6,2.83,18.87,12.15,22.98,12.89,2.81.51,5.69,1.74,11.87,1.72,4.52-.01,7.04-.09,11.17,0,3,.06,4.6.32,8.49,2.17l3.7,1.85s.64.13,4.4,3.32c1.47,1.25,5.11,7.85,5.11,9.77"
          />
          <path
            className={`transition-all duration-[2000ms] ${
              expanded ? 'ease-[cubic-bezier(0.3,2,0.21,0.85)]' : 'ease-[cubic-bezier(0.79,0,0.6,-0.3)]'
            }`}
            style={{
              transform: expanded
                ? 'scale(1) rotate(0deg) translate(0, 0)'
                : 'scale(1.25) rotate(-11.5deg) translate(-100px, -45px)',
              transformOrigin: 'bottom center',
            }}
            d="m591.98,467.54c3,6.83,5.71,8.96,8.57,10.84,6.49,4.28,24.09,5.32,35.94,3.89,0,0,8.07-.26,17.87-3.32,14.94-4.66,17.66-7.62,19.6-9.19,5.11-4.13,9.71-7.16,17.04-17.62,7.09-10.11,5.53-7.52,10.02-15.51,4.24-7.54,3.86-7.6,8.87-16.66.78-1.42,3.77-7.85,8.55-13.98,5.84-7.5,12.13-12.21,15.57-14.62,6.89-4.81,9.36-5.62,16.34-10.6,1.11-.79,4.53-2.66,7.66-6.26,3.77-4.33,4.23-6.42,5.47-9.87,1.56-4.36,2.03-7.58,1.43-11.7-.7-4.79-1.22-5.29-4.02-9.64-2.17-3.37-3.04-4.12-5.3-7.72-2.55-4.09-1.72-2.43-6.32-9.83-2.87-4.62-4.11-7.82-6.32-16.79-1.91-7.77-2.38-10.6-2.62-12.26-.81-5.58-.94-4.89-1.6-11.55-.27-2.79-.8-6.73-1.66-12.57-.35-2.37-.12-6.68-1.77-11.26-1.99-5.52-1.21-5.14-2.19-8.15-1.76-5.42-1.51-6.59-3.26-12.32-.9-2.96-1.62-4.91-3.57-10.09-1.27-3.37-2.89-5.25-4-7.74-1.09-2.44-2-3.69-3.7-6.57-1.6-2.72-3.3-4.93-6.19-7.87-.61-.62-3.43-4.35-7.91-7.05-6.26-3.77-5.79-3.51-7.47-4.16-2.58-1-4.12-2.5-8.49-3.77-7.02-2.03-3.3-.95-7.6-1.91-1.8-.41-7.21-1.33-14.43-.77-2.47.19-4.25.56-6.96,2.49-2.93,2.08-4.69,4.44-5.62,6-2.53,4.26-2.65,6.69-2.74,7.72-.3,3.22-.36,5.92,1.02,9.32.96,2.34.8,2.62,4.28,6.32,1.98,2.11,3.32,2.94,5.68,4.53,3.87,2.61,5.57,2.9,9.19,4.72,0,0,1.15-.06,8.04,3.26h0c2.44,3.25,6.16,5.57,7.72,7.4,0,0,.94,1.66,5.11,6.38l1.85,2.87c2.33,4.13,1.72,4.53,3.7,7.09,0,0,.64,2.04,1.72,3.83l1.7,5.64c1.53,3.32.96,4.83,2.49,8.15l.51,1.94c1.66,4.98,2.25,6.98,2.77,9.55.6,2.99.63,3.03,1,6.53s.56,4.57,1.21,8.55c.87,5.29.78,4.58,1.66,10.09.52,3.26.96,7.79.96,7.79l.83,5.23c.26,3.06.64,5.62.64,5.62.21,3.46.38,5.3.6,7.51,0,0,.11,4.11.11,9.47l-1.72,5.36c-2.17,4.21-3.15,4.7-4.4,7.02,0,0-.86.54-4.6,6.38l-3,4.15c-2.53,4.06-3.02,4.5-4.53,6.96-3.64,5.92-2.95,4.77-4.6,7.47s-1.73,3.42-3.57,6.26c-1.51,2.31-3.24,4.79-5.04,7.66-2.67,4.26-9.13,12.7-9.13,12.7,0,0-12.74,13.21-13.85,14.43-2.6,2.83-18.87,12.15-22.98,12.89-2.81.51-5.69,1.74-11.87,1.72-4.52-.01-7.04-.09-11.17,0-3,.06-4.6.32-8.49,2.17l-3.7,1.85s-.64.13-4.4,3.32c-1.47,1.25-5.11,7.85-5.11,9.77"
          />
        </g>
      </svg>
    )
  }
)
